import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { fetchWithAuth } from "../utils/api";
import { useAuth } from "./AuthContext";
import { useSocket } from "./SocketContext";
import { clockGps } from "../utils/clockGps";
import { sendSave, answerOf, onOutboxSent, subscribeOutbox, SENT_AT } from "../utils/offlineOutbox";

const TimerContext = createContext(null);

/**
 * Global state for the current user's active time clock.
 * Server-authoritative: stores clock_in timestamp, client calculates elapsed live.
 *
 * It's theirs when they may clock in on the web ("Time tracking on web",
 * timeclocks.track_on_web — the permissions /auth/me sends). The running
 * timer is read when the page loads, when the device is back online or the
 * live connection is back, and when the page comes back to the front (iPad
 * Safari reloads and suspends tabs often).
 *
 * Clocking goes through the outbox (utils/offlineOutbox.js — the offline
 * mode) with the time it was tapped (`at`; chariot-backend utils/clockTime.js):
 * with no signal it waits on the device and goes by itself. Until then the
 * clock saves waiting on this device decide what shows — the last one: a
 * clock-in or switch, its timer (`waiting: true`, its id standing for the
 * one the clock-in's answer will give); a clock-out, none — so a reload with
 * no signal still shows it, and a second clock-in is never queued behind one
 * waiting (it would be refused once both went).
 */
const CATEGORY_WORDS = { labor: "work", drive: "driving", break: "a break", left_for_parts: "a parts run", lunch: "lunch", training: "training", comeback: "a comeback", admin: "admin" };
const clockLabel = (verb, payload) => `${verb}${payload?.category ? ` (${CATEGORY_WORDS[payload.category] || payload.category})` : ""}`;
const CLOCK_KINDS = new Set(["clock-in", "clock-switch", "clock-out"]);
// A timer that started with no signal, shown until its clock-in is sent.
const waitingTimer = (saveId, at, payload) => ({
  id: answerOf(saveId), status: "active", clock_in: at, category: payload.category || "labor",
  order_id: payload.order_id || null, service_id: payload.service_id || null, labor_id: payload.labor_id || null,
  description: payload.description || null, waiting: true,
});
export const WAITING_CLOCK_IN = "Your clock-in is waiting for signal and goes by itself. Switch or clock out from it.";

export function TimerProvider({ children }) {
  const { user, can } = useAuth();
  const { socket, isConnected } = useSocket() || {};
  const [serverTimer, setServerTimer] = useState(null);
  const [queued, setQueued] = useState([]); // this person's clock saves waiting on the device
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);

  const userId = user?.id || null;
  const canTrackTime = !!userId && !!can?.("timeclocks.track_on_web");

  // ─── The running timer, from Chariot ────────────────────────────────────────
  // With no answer, what was known stands.
  const refresh = useCallback(async () => {
    if (!userId || !canTrackTime) {
      setServerTimer(null);
      setLoading(false);
      return;
    }
    try {
      const res = await fetchWithAuth("/timeclocks/me/active", { timeout: 15000 });
      if (res.ok) setServerTimer(await res.json());
    } catch (err) {
      console.error("[Timer] Fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, [userId, canTrackTime]);

  useEffect(() => { refresh(); }, [refresh]);

  // Back online, or the page back in front: read it again (one request each).
  useEffect(() => {
    if (!canTrackTime) return undefined;
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("online", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", refresh);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [canTrackTime, refresh]);

  // The live connection back after a drop (not its first connect): whatever changed meanwhile.
  const connection = useRef({ was: isConnected, dropped: false });
  useEffect(() => {
    const c = connection.current;
    if (c.was && !isConnected) c.dropped = true;
    if (isConnected && c.dropped) {
      c.dropped = false;
      refresh();
    }
    c.was = isConnected;
  }, [isConnected, refresh]);

  // ─── Listen for socket events ───────────────────────────────────────────────
  useEffect(() => {
    if (!socket || !userId) return undefined;

    const handleMineChanged = (payload) => {
      // If payload is a full active timer, use it; if it's a "closed" signal, clear state
      if (payload?.status === "active") {
        setServerTimer(payload);
      } else if (payload?.status === "closed" || payload?.status === "voided") {
        setServerTimer((prev) => (prev?.id === payload.id ? null : prev));
      } else {
        // Fallback: refetch
        refresh();
      }
    };

    socket.on("timeclock:mine_changed", handleMineChanged);
    return () => socket.off("timeclock:mine_changed", handleMineChanged);
  }, [socket, userId, refresh]);

  // The clock saves still on this device (a reload keeps them).
  useEffect(() => subscribeOutbox((list) => setQueued(list.filter((s) => CLOCK_KINDS.has(s.kind) && s.state !== "failed"))), []);

  // A clock save made with no signal has gone: the real timer (or none) is Chariot's.
  useEffect(() => onOutboxSent((save, json) => {
    if (save.kind === "clock-in" && json?.status === "active") setServerTimer(json);
    else if (save.kind === "clock-switch" && json?.active?.status === "active") setServerTimer(json.active);
    else if (save.kind === "clock-out" && json?.id) setServerTimer((prev) => (prev?.id === json.id ? null : prev));
  }), []);

  const activeTimer = useMemo(() => {
    if (!canTrackTime) return null;
    const last = queued[queued.length - 1];
    if (!last) return serverTimer;
    if (last.kind === "clock-out") return null;
    return waitingTimer(last.id, last.body?.at || new Date(last.createdAt).toISOString(), last.body || {});
  }, [canTrackTime, queued, serverTimer]);

  // ─── Actions ────────────────────────────────────────────────────────────────
  // The time is the tap's; the save is kept on the device before the GPS fix
  // (up to 10 s) is waited on, so a page closed meanwhile still sends it. With
  // SENT_AT (the device's clock as the save leaves) Chariot counts back from
  // its own clock, so a device clock set wrong neither blocks nor moves it.
  const clockIn = useCallback(async (payload = {}) => {
    if (activeTimer?.waiting) throw new Error(WAITING_CLOCK_IN);
    setPending(true);
    try {
      const at = new Date().toISOString();
      const out = await sendSave({
        method: "POST", path: "/timeclocks/clock-in", body: { ...payload, at, sent_at: SENT_AT }, extra: clockGps(),
        kind: "clock-in", label: clockLabel("Clock in", payload), orderId: payload.order_id,
      });
      if (out.queued) return waitingTimer(out.id, at, payload);
      if (!out.ok) throw new Error(out.json?.error || "Clock in failed");
      setServerTimer(out.json);
      return out.json;
    } finally {
      setPending(false);
    }
  }, [activeTimer]);

  // `timer`: the one to stop — a card's own (the order's Time Clocks tab, the
  // Time Clocks page), else the one running here.
  const clockOut = useCallback(async (payload = {}, timer = activeTimer) => {
    if (!timer) return null;
    setPending(true);
    try {
      const at = new Date().toISOString();
      const out = await sendSave({
        method: "POST", path: `/timeclocks/${timer.id}/clock-out`, body: { ...payload, at, sent_at: SENT_AT }, extra: clockGps(),
        kind: "clock-out", label: "Clock out", orderId: timer.order_id,
      });
      if (!out.queued && !out.ok) throw new Error(out.json?.error || "Clock out failed");
      if (!out.queued) setServerTimer((prev) => (prev?.id === timer.id ? null : prev));
      return out.queued ? { id: timer.id, status: "closed", waiting: true } : out.json;
    } finally {
      setPending(false);
    }
  }, [activeTimer]);

  const switchActivity = useCallback(async (payload = {}) => {
    setPending(true);
    try {
      const at = new Date().toISOString();
      const out = await sendSave({
        method: "POST", path: "/timeclocks/switch", body: { ...payload, at, sent_at: SENT_AT }, extra: clockGps(),
        kind: "clock-switch", label: clockLabel("Switch to", payload), orderId: payload.order_id,
      });
      if (out.queued) return waitingTimer(out.id, at, payload);
      if (!out.ok) throw new Error(out.json?.error || "Switch failed");
      setServerTimer(out.json.active);
      return out.json.active;
    } finally {
      setPending(false);
    }
  }, []);

  const value = { activeTimer, loading, pending, canTrackTime, clockIn, clockOut, switchActivity, refresh };
  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
}

export function useMyTimer() {
  const ctx = useContext(TimerContext);
  if (!ctx) return { activeTimer: null, canTrackTime: false, clockIn: () => {}, clockOut: () => {}, switchActivity: () => {}, refresh: () => {} };
  return ctx;
}

/**
 * Hook to get a ticking elapsed-seconds counter for a timer start time.
 * Updates every second. Returns "HH:MM:SS" formatted string.
 */
export function useElapsedTime(startIso) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!startIso) return;
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, [startIso]);

  if (!startIso) return "00:00:00";
  const seconds = Math.max(0, Math.floor((now - new Date(startIso)) / 1000));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
